import { defineSlotRecipe } from '@chakra-ui/react';

/*
 * Design system source: .claude/skills/monetizenow-design/preview/modal-drawer.html
 *
 * Drawer shell: same shadow.lg as modal, body surface white (was drawerSidebar gray).
 * Header: 14px 18px, 14px semibold title, 1px border-subtle bottom, close X 24×24.
 * Drawers in Chakra don't round the edge against the viewport by default; we leave
 * radius off here (attaching to viewport edge) to match typical usage.
 *
 * v3 port notes:
 * - Slots renamed from v2's `drawerAnatomy`: `overlay` → `backdrop`, `dialog` →
 *   `content`, `closeButton` → `closeTrigger`. `header`/`body`/`footer` are
 *   unchanged names. `positioner`/`title`/`description` are new slots with no
 *   v2 equivalent (Drawer is built directly on Ark's Dialog machine in v3,
 *   same as Modal/Dialog — confirmed via `Drawer.Context: DialogContext` at
 *   runtime).
 * - Chakra's built-in `drawer` recipe deep-merges *underneath* this one, same
 *   mechanism as Card/Tabs/Menu/Dialog — confirmed empirically via a
 *   throwaway `createSystem`. `defaultVariants: {size: 'xs', placement: 'end'}`
 *   — every real call site in this app passes both explicitly (confirmed via
 *   grep), so the built-in's 320px `xs` default is never actually reached in
 *   practice; not neutralized here for that reason, same judgment call as
 *   Tag's un-neutralized `sm`/`md` size variants.
 * - `placement` values are logical (`start`/`end`/`top`/`bottom`), not
 *   physical (`left`/`right`) — translated at each call site, not here.
 * - v2's own `defaultProps: { variant: 'right' }` referenced a `variant` this
 *   theme never actually defined any styling for (no `variants` block in the
 *   old file at all) — already dead in v2 terms, dropped rather than ported.
 * - Only `xl` is customized here, matching v2's own `sizes` object (which
 *   only ever touched `xl`) — every other size this app uses (`sm`/`md`/`lg`)
 *   falls through to the built-in's own maxW mapping unchanged, same as
 *   Dialog.ts's judgment call for its own `sm`/`md`/`lg`.
 */

export const drawerTheme = defineSlotRecipe({
  slots: [
    'backdrop',
    'positioner',
    'content',
    'header',
    'body',
    'footer',
    'title',
    'description',
    'closeTrigger',
  ],
  base: {
    backdrop: {
      bg: 'rgba(24, 36, 60, 0.86)',
    },
    // The built-in sizes the positioner 100vw × 100dvh, which counts the page
    // scrollbars. Most drawers here keep the page scrollable
    // (`preventScroll={false}`), so the content's end edge, close X included,
    // slid under the vertical scrollbar and its footer under the horizontal
    // one. Pinning to the viewport edges stops at the scrollbars, as v2's
    // `right: 0` content did.
    positioner: {
      inset: 0,
      width: 'auto',
      height: 'auto',
    },
    content: {
      boxShadow: 'lg',
      bg: 'bg.surface',
    },
    header: {
      // v2's header was a plain block. The built-in makes it a centered flex
      // row with a gap, which shrinks a lone `justify="space-between"` MFlex
      // child to its content (PreviewPdfDrawer, NotesDrawer, QuoteAlerts).
      display: 'block',
      alignItems: 'normal',
      gap: 0,
      fontSize: 'lg', // 14px
      fontWeight: 600,
      color: 'fg.strong',
      borderBottom: '1px solid',
      borderColor: 'border.subtle',
      py: '14px',
      px: '18px',
    },
    body: {
      bg: 'bg.surface',
      p: '18px',
    },
    footer: {
      borderTop: '1px solid',
      borderColor: 'border.subtle',
      py: 3,
      px: '18px',
    },
    // Matches the Dialog close X (`MCenterModal`).
    closeTrigger: {
      top: 3,
      insetEnd: 3,
      borderRadius: 'sm',
      // v3's preflight no longer gives buttons `cursor: pointer`.
      cursor: 'pointer',
      // Consumers pass a bare `<Icon asChild><MdClose /></Icon>`, which would
      // otherwise size to the content's 13px font.
      _icon: { boxSize: 5 },
      _hover: { bg: 'bg.hover' },
    },
  },
  variants: {
    size: {
      xl: {
        content: { maxW: '80vw' },
      },
    },
  },
});

export default drawerTheme;
